import React, { useState, useCallback, useEffect } from "react";
import { PageHeader, Card, fmt, Tag } from "../components/UI";
import DataTable from "../components/DataTable";
import ReportCharts from "../components/ReportCharts";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Search, Loader2, X, User2, Phone, MapPin, Calendar, Award, Flame, Wallet, BookOpen, ArrowDownCircle, ArrowUpCircle, AlertTriangle, CheckCircle2, Wrench, Database, Download, FileSpreadsheet, RefreshCw } from "lucide-react";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return String(s).slice(0, 10); }
}

function sourceTone(src) {
  const s = String(src || "").toLowerCase();
  if (s.includes("field")) return "teal";
  if (s.includes("whatsapp")) return "emerald";
  return "amber";
}

const columns = [
  { key: "name", label: "Customer", filter: "text" },
  { key: "mobile", label: "Mobile", filter: "text" },
  { key: "erp_outlet_id", label: "ERP Outlet", filter: "text", render: (r) => r.erp_outlet_id || "—" },
  { key: "clinic_name", label: "Clinic", render: (r) => r.clinic_name || "—" },
  { key: "city", label: "City", render: (r) => r.city || "—" },
  { key: "active_points", label: "Active", align: "right", render: (r) => <b>{fmt(r.active_points)}</b> },
  { key: "earned_points", label: "Earned", align: "right", render: (r) => fmt(r.earned_points) },
  { key: "burned_points", label: "Burned", align: "right", render: (r) => <span className="text-rose-600">{fmt(r.burned_points)}</span> },
  { key: "registered_at", label: "Registered", accessor: (r) => r.registered_at, render: (r) => fmtDate(r.registered_at) },
];

const monthOf = (s) => (s ? String(s).slice(0, 7) : null);
const searchCharts = [
  { id: "cs-top-active", type: "bar", title: "Top customers by active points", valueLabel: "Active points", groupBy: (r) => r.name, value: (r) => r.active_points || 0, sort: "value_desc", limit: 10, labelWidth: 130 },
  { id: "cs-city", type: "bar", title: "Customers by city", valueLabel: "Customers", groupBy: (r) => r.city, sort: "value_desc", limit: 10 },
  { id: "cs-trend", type: "area", title: "Registrations over time", subtitle: "Monthly, for this result set", valueLabel: "Registrations", groupBy: (r) => monthOf(r.registered_at), sort: "time", color: "#0F766E" },
];

function Stat({ icon: Icon, label, value, tone = "slate" }) {
  const tones = { slate: "text-slate-700", emerald: "text-emerald-600", rose: "text-rose-600", teal: "text-teal-700" };
  return (
    <div className="surface p-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-slate-100 grid place-items-center text-slate-500"><Icon size={16} /></div>
      <div>
        <div className="small-caps text-slate-500">{label}</div>
        <div className={`text-lg font-semibold ${tones[tone]}`}>{value}</div>
      </div>
    </div>
  );
}

function Passbook({ physioId, loading, data }) {
  const [tab, setTab] = useState("earned");
  if (loading) {
    return (
      <div className="mt-4 py-8 text-center text-slate-400 inline-flex items-center gap-2 justify-center w-full" data-testid="passbook-loading">
        <Loader2 size={16} className="animate-spin" /> Loading passbook…
      </div>
    );
  }
  if (!data) return null;
  const earned = data.earned || [];
  const reds = data.redemptions || [];
  return (
    <div className="mt-5 border-t border-slate-100 pt-4" data-testid="passbook">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen size={15} className="text-teal-700" />
        <span className="font-semibold text-slate-800 text-[13.5px]">Points Passbook</span>
        <span className="text-[11px] text-slate-400">Earned points &amp; redemptions with their source</span>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setTab("earned")} className={`!py-1.5 text-[12.5px] inline-flex items-center gap-1.5 ${tab === "earned" ? "btn-primary" : "btn-secondary"}`} data-testid="passbook-tab-earned">
          <ArrowDownCircle size={13} /> Points Earned ({fmt(data.earned_count || earned.length)})
        </button>
        <button onClick={() => setTab("redemptions")} className={`!py-1.5 text-[12.5px] inline-flex items-center gap-1.5 ${tab === "redemptions" ? "btn-primary" : "btn-secondary"}`} data-testid="passbook-tab-redemptions">
          <ArrowUpCircle size={13} /> Redemptions ({fmt(data.redemption_count || reds.length)})
        </button>
        <div className="ml-auto text-[12px] text-slate-500">
          {tab === "earned"
            ? <>Total earned: <b className="text-emerald-600">{fmt(data.summary?.total_earned)}</b></>
            : <>Total redeemed: <b className="text-rose-600">{fmt(data.summary?.total_redeemed)}</b></>}
        </div>
      </div>

      {tab === "earned" ? (
        <div className="overflow-auto max-h-[420px] rounded-lg border border-slate-100" data-testid="passbook-earned-table">
          <table className="w-full text-[12.5px]">
            <thead className="bg-slate-50 text-slate-500 sticky top-0">
              <tr>
                <th className="text-left font-medium px-3 py-2">Date</th>
                <th className="text-left font-medium px-3 py-2">Product / Order</th>
                <th className="text-left font-medium px-3 py-2">Type</th>
                <th className="text-left font-medium px-3 py-2">Source</th>
                <th className="text-right font-medium px-3 py-2">Points</th>
              </tr>
            </thead>
            <tbody>
              {earned.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No points earned yet.</td></tr>}
              {earned.map((e, i) => (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`passbook-earned-row-${i}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(e.date)}</td>
                  <td className="px-3 py-2">{e.product || "—"}</td>
                  <td className="px-3 py-2"><Tag tone={e.kind === "Active" ? "emerald" : e.kind === "Carry-Forward" ? "teal" : e.kind === "Correction" ? "amber" : "slate"}>{e.kind}</Tag></td>
                  <td className="px-3 py-2"><Tag tone={sourceTone(e.source_label)}>{e.source_label}</Tag></td>
                  <td className={`px-3 py-2 text-right font-semibold ${e.points >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{e.points >= 0 ? "+" : "−"}{fmt(Math.abs(e.points))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="overflow-auto max-h-[420px] rounded-lg border border-slate-100" data-testid="passbook-redemptions-table">
          <table className="w-full text-[12.5px]">
            <thead className="bg-slate-50 text-slate-500 sticky top-0">
              <tr>
                <th className="text-left font-medium px-3 py-2">Date</th>
                <th className="text-left font-medium px-3 py-2">Reward</th>
                <th className="text-left font-medium px-3 py-2">Status</th>
                <th className="text-left font-medium px-3 py-2">Source</th>
                <th className="text-right font-medium px-3 py-2">Points</th>
              </tr>
            </thead>
            <tbody>
              {reds.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No redemptions yet.</td></tr>}
              {reds.map((r, i) => (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`passbook-redemption-row-${i}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2">{r.reward_name}</td>
                  <td className="px-3 py-2"><Tag tone="slate">{r.status}</Tag></td>
                  <td className="px-3 py-2"><Tag tone={sourceTone(r.source_label)}>{r.source_label}</Tag></td>
                  <td className="px-3 py-2 text-right font-semibold text-rose-600">-{fmt(r.points)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function CustomerSearch() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [searched, setSearched] = useState(false);
  const [passbook, setPassbook] = useState(null);
  const [pbLoading, setPbLoading] = useState(false);
  const { user } = useAuth();
  const canReconcile = ["ho_admin", "super_admin"].includes(user?.role);
  const [unsourced, setUnsourced] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconMsg, setReconMsg] = useState("");

  const loadUnsourced = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/unsourced-points");
      setUnsourced(data);
    } catch (e) { setUnsourced(null); }
  }, []);

  useEffect(() => { loadUnsourced(); }, [loadUnsourced]);

  const runReconcile = useCallback(async () => {
    setReconciling(true); setReconMsg("");
    try {
      const { data } = await api.post("/admin/reconcile-point-sources");
      setReconMsg(`Tagged ${fmt(data.applied)} physio${data.applied === 1 ? "" : "s"} — ${fmt(data.total_active_points + data.total_pending_points)} points now traced to "Admin Panel (Opening Balance)".`);
      await loadUnsourced();
    } catch (e) { setReconMsg(formatError(e)); }
    finally { setReconciling(false); }
  }, [loadUnsourced]);

  const openCustomer = useCallback(async (r) => {
    setSelected(r);
    setPassbook(null);
    setWalletReconMsg("");
    setPbLoading(true);
    try {
      const { data } = await api.get(`/admin/customer-search/${r.id}/passbook`);
      setPassbook(data);
    } catch (e) { setPassbook(null); }
    finally { setPbLoading(false); }
  }, []);

  // Point 53 — Admin Panel (Legacy) opening-balance source data for reconciliation
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [legacyData, setLegacyData] = useState(null);
  const [legacyLoading, setLegacyLoading] = useState(false);

  const openLegacy = useCallback(async () => {
    setLegacyOpen(true);
    if (legacyData) return;
    setLegacyLoading(true);
    try {
      const { data } = await api.get("/admin/legacy-points");
      setLegacyData(data);
    } catch (e) { setLegacyData({ items: [], count: 0, error: formatError(e) }); }
    finally { setLegacyLoading(false); }
  }, [legacyData]);

  const downloadLegacyCsv = useCallback(() => {
    const items = legacyData?.items || [];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["Mobile", "Physio Name", "Clinic", "ERP Outlet Id", "Active Points", "Pending Points", "Total Points", "Source", "First Added"];
    const lines = items.map((it) => [it.mobile, it.name, it.clinic_name, it.erp_outlet_id, it.active_points, it.pending_points, it.points, it.source_label || "Admin Panel", String(it.date || "").slice(0, 10)].map(esc).join(","));
    const blob = new Blob([[head.map(esc).join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "admin_panel_points_source.csv";
    a.click();
  }, [legacyData]);

  // Point 54 — manual points correction
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjusting, setAdjusting] = useState(false);
  const [adjustMsg, setAdjustMsg] = useState("");

  const openAdjust = useCallback(() => {
    setAdjustTarget(selected ? String(selected.active_points ?? "") : "");
    setAdjustReason("");
    setAdjustMsg("");
    setAdjustOpen(true);
  }, [selected]);

  const submitAdjust = useCallback(async () => {
    if (!selected) return;
    setAdjusting(true); setAdjustMsg("");
    try {
      const { data } = await api.post("/admin/points-adjust", {
        mobile: selected.mobile,
        target_points: parseFloat(adjustTarget),
        reason: adjustReason,
      });
      setSelected((s) => (s ? { ...s, active_points: data.after } : s));
      setAdjustMsg(`Updated ${data.name} — points set from ${fmt(data.before)} to ${fmt(data.after)} (Δ ${data.delta >= 0 ? "+" : ""}${fmt(data.delta)}).`);
      await openCustomer({ ...selected, active_points: data.after });
      setTimeout(() => setAdjustOpen(false), 1200);
    } catch (e) { setAdjustMsg(formatError(e)); }
    finally { setAdjusting(false); }
  }, [selected, adjustTarget, adjustReason, openCustomer]);

  // Point 65 — reconcile a wallet's buckets from its transaction ledger (no new txn)
  const [reconcilingWallet, setReconcilingWallet] = useState(false);
  const [walletReconMsg, setWalletReconMsg] = useState("");

  const reconcileWallet = useCallback(async () => {
    if (!selected) return;
    if (!window.confirm(`Recompute ${selected.name}'s points from their full transaction ledger? This re-sums every earned/correction entry and updates the Active, Annual and Quarterly balances to match. No new transaction is created.`)) return;
    setReconcilingWallet(true); setWalletReconMsg("");
    try {
      const { data } = await api.post(`/admin/customer-search/${selected.id}/reconcile-wallet`);
      const a = data.after || {};
      setSelected((s) => (s ? { ...s, active_points: a.active } : s));
      setWalletReconMsg(data.changed
        ? `Reconciled from ledger — Active ${fmt(data.before?.active)} → ${fmt(a.active)}, Annual ${fmt(data.before?.annual_points)} → ${fmt(a.annual_points)}.`
        : `Already in sync — Active ${fmt(a.active)}, Annual ${fmt(a.annual_points)}. No change needed.`);
      await openCustomer({ ...selected, active_points: a.active });
    } catch (e) { setWalletReconMsg(formatError(e)); }
    finally { setReconcilingWallet(false); }
  }, [selected, openCustomer]);

  const search = useCallback(async () => {
    setLoading(true); setError(""); setSelected(null); setPassbook(null);
    try {
      const { data } = await api.get(`/admin/customer-search?q=${encodeURIComponent(q.trim())}&limit=200`);
      setRows(data.rows || []);
      setCount(data.count || 0);
      setSearched(true);
    } catch (e) { setError(formatError(e)); }
    finally { setLoading(false); }
  }, [q]);

  return (
    <div className="space-y-5" data-testid="customer-search">
      <PageHeader
        kicker="Administration · Support"
        title="Customer Search"
        subtitle="Look up any physio by name, mobile, ERP outlet id or clinic. View points earned, points burned (redeemed), balances and registration date."
        testId="customer-search-header"
      />

      <div className="flex justify-end">
        <button onClick={openLegacy} className="btn-secondary !py-2 inline-flex items-center gap-2 text-[12.5px]" data-testid="legacy-points-btn">
          <Database size={14} /> Admin-Added Points
        </button>
      </div>

      {unsourced && unsourced.affected_count > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5 flex flex-wrap items-center gap-3" data-testid="unsourced-banner">
          <AlertTriangle size={18} className="text-amber-600 shrink-0" />
          <div className="flex-1 min-w-[240px]">
            <div className="text-[13.5px] font-semibold text-amber-900" data-testid="unsourced-count">
              {fmt(unsourced.affected_count)} physio{unsourced.affected_count === 1 ? "" : "s"} {unsourced.affected_count === 1 ? "has" : "have"} earned points with no recorded source
            </div>
            <div className="text-[12px] text-amber-700">
              {fmt(unsourced.total_active_points)} active + {fmt(unsourced.total_pending_points)} pending points (migrated / opening balances). Tag them so every point shows a source in the passbook &amp; reports.
            </div>
          </div>
          {canReconcile ? (
            <button onClick={runReconcile} disabled={reconciling} className="btn-primary !py-2 inline-flex items-center gap-2" data-testid="reconcile-btn">
              {reconciling ? <Loader2 size={14} className="animate-spin" /> : <Wrench size={14} />}
              {reconciling ? "Tagging…" : "Tag source & reconcile"}
            </button>
          ) : (
            <span className="text-[12px] text-amber-700">Ask an HO Admin to reconcile.</span>
          )}
        </div>
      )}
      {unsourced && unsourced.affected_count === 0 && reconMsg && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2 text-[13px] text-emerald-800" data-testid="reconcile-done">
          <CheckCircle2 size={16} className="text-emerald-600" /> {reconMsg} Every earned point now has a recorded source.
        </div>
      )}
      {reconMsg && unsourced && unsourced.affected_count > 0 && (
        <div className="text-[12.5px] text-slate-600 px-1" data-testid="reconcile-msg">{reconMsg}</div>
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1 flex-1 min-w-[260px]">
            <label className="small-caps text-slate-500">Search customers</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder="Name, mobile, ERP outlet id or clinic…"
                className="pl-9 !py-2 w-full"
                data-testid="customer-search-input"
              />
            </div>
          </div>
          <button onClick={search} disabled={loading} className="btn-primary !py-2 inline-flex items-center gap-1.5" data-testid="customer-search-btn">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Search
          </button>
          {q && <button onClick={() => { setQ(""); setRows([]); setCount(0); setSearched(false); setSelected(null); }} className="btn-secondary !py-2 inline-flex items-center gap-1.5"><X size={13} /> Clear</button>}
          <div className="ml-auto text-[12px] text-slate-500">{searched ? `${fmt(count)} result${count === 1 ? "" : "s"}` : "Enter a search term (blank returns recent customers)"}</div>
        </div>
        {error && <div className="mt-3 text-[12px] text-rose-700">{error}</div>}
      </Card>

      {selected && (
        <Card title={selected.name} subtitle={`ERP ${selected.erp_outlet_id || "—"} · ${selected.clinic_name || "No clinic"}`} action={
          <div className="flex items-center gap-2">
            {canReconcile && (
              <button onClick={reconcileWallet} disabled={reconcilingWallet} className="btn-secondary !py-1.5 inline-flex items-center gap-1" data-testid="reconcile-wallet-btn" title="Recompute Active/Annual/Quarterly from the transaction ledger">
                {reconcilingWallet ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Reconcile from Ledger
              </button>
            )}
            {canReconcile && (
              <button onClick={openAdjust} className="btn-secondary !py-1.5 inline-flex items-center gap-1" data-testid="adjust-points-btn"><Wrench size={13} /> Adjust Points</button>
            )}
            <button onClick={() => setSelected(null)} className="btn-secondary !py-1.5 inline-flex items-center gap-1"><X size={13} /> Close</button>
          </div>
        }>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="customer-detail">
            <Stat icon={Wallet} label="Active points" value={fmt(selected.active_points)} tone="teal" />
            <Stat icon={Award} label="Points earned" value={fmt(selected.earned_points)} tone="emerald" />
            <Stat icon={Flame} label="Points burned" value={fmt(selected.burned_points)} tone="rose" />
            <Stat icon={Award} label="Redemptions" value={fmt(selected.redemption_count)} />
            <Stat icon={Phone} label="Mobile" value={selected.mobile || "—"} />
            <Stat icon={MapPin} label="City / State" value={`${selected.city || "—"}${selected.state ? ", " + selected.state : ""}`} />
            <Stat icon={User2} label="Assigned DSE" value={selected.dse || "—"} />
            <Stat icon={Calendar} label="Registered on" value={fmtDate(selected.registered_at)} />
          </div>
          {walletReconMsg && (
            <div className="mt-3 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-[12.5px] text-teal-800 inline-flex items-center gap-2" data-testid="wallet-reconcile-msg">
              <CheckCircle2 size={14} /> {walletReconMsg}
            </div>
          )}
          <Passbook physioId={selected.id} loading={pbLoading} data={passbook} />
        </Card>
      )}

      {searched && rows.length > 0 && (
        <ReportCharts rows={rows} charts={searchCharts} testId="customer-search" title="Customer insights" />
      )}

      {searched && (
        <Card>
          <DataTable
            columns={columns}
            data={rows}
            rowKey="id"
            testIdPrefix="customer-search"
            searchableKeys={["name", "mobile", "erp_outlet_id", "clinic_name", "city"]}
            exportFilename="customer-search"
            onRowClick={(r) => openCustomer(r)}
            paginated
            pageSize={25}
          />
        </Card>
      )}

      {adjustOpen && selected && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="adjust-modal" onClick={() => !adjusting && setAdjustOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mt-20" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
              <Wrench size={17} className="text-teal-700" />
              <div className="flex-1">
                <div className="font-semibold text-slate-800 text-[14px]">Adjust Points — {selected.name}</div>
                <div className="text-[12px] text-slate-500">Set the Active points to an exact value (rounded down to a whole number).</div>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <div className="surface p-3 flex items-center justify-between">
                <span className="small-caps text-slate-500">Current active points</span>
                <span className="text-lg font-semibold text-teal-700">{fmt(selected.active_points)}</span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="small-caps text-slate-500">New target points</label>
                <input type="number" step="any" value={adjustTarget} onChange={(e) => setAdjustTarget(e.target.value)} placeholder="e.g. 2411" className="!py-2 w-full" data-testid="adjust-target-input" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="small-caps text-slate-500">Reason (for audit)</label>
                <input value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} placeholder="e.g. backend correction" className="!py-2 w-full" data-testid="adjust-reason-input" />
              </div>
              {adjustMsg && <div className="text-[12.5px] text-slate-700" data-testid="adjust-msg">{adjustMsg}</div>}
              <div className="flex items-center justify-end gap-2 pt-1">
                <button onClick={() => setAdjustOpen(false)} disabled={adjusting} className="btn-secondary !py-2">Cancel</button>
                <button onClick={submitAdjust} disabled={adjusting || adjustTarget === ""} className="btn-primary !py-2 inline-flex items-center gap-2" data-testid="adjust-save-btn">
                  {adjusting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Set Points
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {legacyOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="legacy-modal" onClick={() => setLegacyOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mt-10 mb-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
              <FileSpreadsheet size={18} className="text-teal-700" />
              <div className="flex-1">
                <div className="font-semibold text-slate-800 text-[14px]">Admin-Added Points (for reconciliation)</div>
                <div className="text-[12px] text-slate-500">Every point added from the admin panel / front-end — opening-balance uploads, carry-forward imports &amp; manual corrections. Reconcile against your source file (nothing added here is missing).</div>
              </div>
              <button onClick={() => setLegacyOpen(false)} className="btn-secondary !py-1.5 inline-flex items-center gap-1" data-testid="legacy-close"><X size={13} /> Close</button>
            </div>

            {legacyLoading ? (
              <div className="py-12 text-center text-slate-400 inline-flex items-center gap-2 justify-center w-full"><Loader2 size={16} className="animate-spin" /> Loading legacy points…</div>
            ) : (
              <div className="p-5">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <div className="surface p-3 flex-1 min-w-[120px]">
                    <div className="small-caps text-slate-500">Physios</div>
                    <div className="text-lg font-semibold text-slate-800" data-testid="legacy-count">{fmt(legacyData?.count)}</div>
                  </div>
                  <div className="surface p-3 flex-1 min-w-[120px]">
                    <div className="small-caps text-slate-500">Active points</div>
                    <div className="text-lg font-semibold text-emerald-600">{fmt(legacyData?.total_active)}</div>
                  </div>
                  <div className="surface p-3 flex-1 min-w-[120px]">
                    <div className="small-caps text-slate-500">Pending points</div>
                    <div className="text-lg font-semibold text-amber-600">{fmt(legacyData?.total_pending)}</div>
                  </div>
                  <div className="surface p-3 flex-1 min-w-[120px]">
                    <div className="small-caps text-slate-500">Total points</div>
                    <div className="text-lg font-semibold text-teal-700">{fmt(legacyData?.total_points)}</div>
                  </div>
                  <button onClick={downloadLegacyCsv} disabled={!legacyData?.items?.length} className="btn-primary !py-2 inline-flex items-center gap-2" data-testid="legacy-download-csv">
                    <Download size={14} /> Download CSV
                  </button>
                </div>

                <div className="overflow-auto max-h-[52vh] rounded-lg border border-slate-100" data-testid="legacy-table">
                  <table className="w-full text-[12.5px]">
                    <thead className="bg-slate-50 text-slate-500 sticky top-0">
                      <tr>
                        <th className="text-left font-medium px-3 py-2">Mobile</th>
                        <th className="text-left font-medium px-3 py-2">Physio</th>
                        <th className="text-left font-medium px-3 py-2">ERP Outlet Id</th>
                        <th className="text-left font-medium px-3 py-2">Source</th>
                        <th className="text-right font-medium px-3 py-2">Active</th>
                        <th className="text-right font-medium px-3 py-2">Pending</th>
                        <th className="text-right font-medium px-3 py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!legacyData?.items || legacyData.items.length === 0) && (
                        <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No admin-added points recorded.</td></tr>
                      )}
                      {(legacyData?.items || []).map((it, i) => (
                        <tr key={it.physio_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`legacy-row-${i}`}>
                          <td className="px-3 py-2 font-medium text-slate-700">{it.mobile || "—"}</td>
                          <td className="px-3 py-2">{it.name}<span className="text-slate-400"> · {it.clinic_name || "—"}</span></td>
                          <td className="px-3 py-2 text-slate-500">{it.erp_outlet_id || "—"}</td>
                          <td className="px-3 py-2 text-slate-500 text-[11.5px]">{it.source_label || "Admin Panel"}</td>
                          <td className="px-3 py-2 text-right text-emerald-600">{fmt(it.active_points)}</td>
                          <td className="px-3 py-2 text-right text-amber-600">{fmt(it.pending_points)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-teal-700">{fmt(it.points)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
